import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { User, Mail, Lock, Camera, Save, X, ArrowLeft, Film, Shield, Upload } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

const AdminProfile = () => {
  const navigate = useNavigate()
  const { isAuthenticated, currentAdmin, adminRole, logout } = useAuth()
  const { register, handleSubmit, formState: { errors }, watch, reset } = useForm()
  const [profileData, setProfileData] = useState(null)
  const [profileImage, setProfileImage] = useState(null)
  const [showPasswordChange, setShowPasswordChange] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const newPassword = watch('newPassword')

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login')
      return
    }

    loadProfileData()
  }, [isAuthenticated, currentAdmin, navigate])

  const loadProfileData = () => {
    if (currentAdmin === 'admin') {
      const savedImage = localStorage.getItem('shikdar_films_admin_profile_image')
      const savedFullName = localStorage.getItem('shikdar_films_admin_fullname') || 'Administrator'
      const savedEmail = localStorage.getItem('shikdar_films_admin_email') || 'admin@shikdarfilms.com'
      
      setProfileData({
        username: 'admin',
        fullName: savedFullName,
        email: savedEmail,
        role: 'super-admin'
      })
      
      if (savedImage) {
        setProfileImage(savedImage)
      }
    } else {
      const admins = JSON.parse(localStorage.getItem('shikdar_films_admins') || '[]')
      const admin = admins.find(a => a.username === currentAdmin)
      
      if (admin) {
        setProfileData(admin)
        if (admin.profileImage) {
          setProfileImage(admin.profileImage)
        }
      }
    }
  }

  const hashPassword = (password) => {
    let hash = 0
    for (let i = 0; i < password.length; i++) {
      const char = password.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return hash.toString(36)
  }

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file')
        return
      }

      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size must be less than 2MB')
        return
      }

      const reader = new FileReader()
      reader.onload = (event) => {
        setProfileImage(event.target.result)
        toast.success('Profile image updated!')
      }
      reader.readAsDataURL(file)
    }
  }

  const removeProfileImage = () => {
    setProfileImage(null)
    toast.success('Profile image removed')
  }

  const onSubmitProfile = async (data) => {
    setIsLoading(true)

    try {
      if (currentAdmin === 'admin') {
        localStorage.setItem('shikdar_films_admin_fullname', data.fullName)
        localStorage.setItem('shikdar_films_admin_email', data.email)
        
        if (profileImage) {
          localStorage.setItem('shikdar_films_admin_profile_image', profileImage)
        } else {
          localStorage.removeItem('shikdar_films_admin_profile_image')
        }
      } else {
        const admins = JSON.parse(localStorage.getItem('shikdar_films_admins') || '[]')
        const updatedAdmins = admins.map(admin => 
          admin.username === currentAdmin
            ? { ...admin, fullName: data.fullName, email: data.email, profileImage: profileImage }
            : admin
        )
        localStorage.setItem('shikdar_films_admins', JSON.stringify(updatedAdmins))
      }

      loadProfileData()
      toast.success('Profile updated successfully!')
    } catch (error) {
      toast.error('Failed to update profile')
    }

    setIsLoading(false)
  }

  const onSubmitPassword = async (data) => {
    setIsLoading(true)

    try {
      if (currentAdmin === 'admin') {
        const currentPasswordHash = localStorage.getItem('shikdar_films_default_admin_password')
        
        if (currentPasswordHash && hashPassword(data.currentPassword) !== currentPasswordHash) {
          toast.error('Current password is incorrect')
          setIsLoading(false)
          return
        } else if (!currentPasswordHash && data.currentPassword !== 'admin123') {
          toast.error('Current password is incorrect')
          setIsLoading(false)
          return
        }

        localStorage.setItem('shikdar_films_default_admin_password', hashPassword(data.newPassword))
      } else {
        const admins = JSON.parse(localStorage.getItem('shikdar_films_admins') || '[]')
        const admin = admins.find(a => a.username === currentAdmin)
        
        if (!admin || admin.password !== hashPassword(data.currentPassword)) {
          toast.error('Current password is incorrect')
          setIsLoading(false)
          return
        }

        const updatedAdmins = admins.map(a => 
          a.username === currentAdmin
            ? { ...a, password: hashPassword(data.newPassword) }
            : a
        )
        localStorage.setItem('shikdar_films_admins', JSON.stringify(updatedAdmins))
      }

      toast.success('Password changed successfully!')
      setShowPasswordChange(false)
      reset()
    } catch (error) {
      toast.error('Failed to change password')
    }

    setIsLoading(false)
  }

  const getRoleBadgeColor = (role) => {
    const colors = {
      'super-admin': 'bg-purple-100 text-purple-800',
      'admin': 'bg-blue-100 text-blue-800',
      'editor': 'bg-green-100 text-green-800',
      'viewer': 'bg-gray-100 text-gray-800'
    }
    return colors[role] || colors.viewer
  }

  if (!profileData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-bengal-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-bengal-green to-bengal-orange rounded-lg flex items-center justify-center">
                <Film className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 section-title">Admin Profile</h1>
                <p className="text-sm text-gray-600">Manage your account settings</p>
              </div>
            </div>
            <Link
              to="/admin/dashboard"
              className="inline-flex items-center gap-2 text-bengal-green hover:text-bengal-orange font-medium transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6 text-center">
              <div className="relative inline-block mb-4">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 mx-auto">
                  {profileImage ? (
                    <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-bengal-green">
                      <User className="w-16 h-16 text-white" />
                    </div>
                  )}
                </div>
                <label className="absolute bottom-0 right-0 w-10 h-10 bg-bengal-orange hover:bg-bengal-red rounded-full flex items-center justify-center cursor-pointer shadow-lg transition-colors">
                  <Camera className="w-5 h-5 text-white" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              </div>

              {profileImage && (
                <button
                  onClick={removeProfileImage}
                  className="text-sm text-red-600 hover:text-red-700 mb-4"
                >
                  Remove Photo
                </button>
              )}

              <h2 className="text-xl font-bold text-gray-900 mb-1">{profileData.fullName}</h2>
              <p className="text-gray-600 mb-3">@{profileData.username}</p>
              
              <div className="flex items-center justify-center gap-2 mb-4">
                <Shield className="w-4 h-4 text-gray-500" />
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(profileData.role)}`}>
                  {profileData.role?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </span>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowPasswordChange(!showPasswordChange)}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  <Lock className="w-4 h-4" />
                  Change Password
                </button>
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-8 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Personal Information</h3>
              
              <form onSubmit={handleSubmit(onSubmitProfile)} className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      id="fullName"
                      defaultValue={profileData.fullName}
                      {...register('fullName', { required: 'Full name is required' })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                    />
                  </div>
                  {errors.fullName && <p className="mt-1 text-sm text-red-600">{errors.fullName.message}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      defaultValue={profileData.email}
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' }
                      })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                    />
                  </div>
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    value={profileData.username}
                    disabled
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500">Username cannot be changed</p>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-bengal-green hover:bg-bengal-orange text-white font-semibold px-6 h-12 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-5 h-5" />
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      reset()
                      loadProfileData()
                    }}
                    className="px-6 h-12 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>

            {/* Password Change Form */}
            {showPasswordChange && (
              <div className="bg-white rounded-xl shadow-md p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Change Password</h3>
                  <button
                    onClick={() => setShowPasswordChange(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmitPassword)} className="space-y-6">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Current Password *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="password"
                        id="currentPassword"
                        {...register('currentPassword', { required: 'Current password is required' })}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                      />
                    </div>
                    {errors.currentPassword && <p className="mt-1 text-sm text-red-600">{errors.currentPassword.message}</p>}
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      New Password *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="password"
                        id="newPassword"
                        {...register('newPassword', { 
                          required: 'New password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters' }
                        })}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                      />
                    </div>
                    {errors.newPassword && <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm New Password *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="password"
                        id="confirmPassword"
                        {...register('confirmPassword', { 
                          required: 'Please confirm password',
                          validate: value => value === newPassword || 'Passwords do not match'
                        })}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                      />
                    </div>
                    {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-bengal-green hover:bg-bengal-orange text-white font-semibold px-6 h-12 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Changing Password...' : 'Change Password'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminProfile